/**
 * Two tones for a failed read.
 *
 * - Nothing answered: neutral, and it says only "Not connected". No status, no
 *   proxy wording, no source id — the header above always names the selected
 *   source, so which one is silent stays unambiguous without transport detail
 *   on screen.
 * - The source answered with a failure (400, 409, 500): a real fault, so the
 *   message renders verbatim in the destructive tone.
 *
 * No "change connection" affordance here: switching to the mock to make a state
 * disappear is a decision the user makes from the header dropdown, looking at
 * the source's name.
 */
import { AlertCircle, RefreshCw, Unplug } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { failureHeadline, isNotConnected, type DataLayerFailure } from '@/lib/dataLayer/errors'

export function DataLayerUnavailable({
  failure,
  sourceId,
  onRetry,
}: {
  failure: DataLayerFailure
  /** The source that was being read, or null if the allowlist never answered. */
  sourceId: string | null
  onRetry: () => void
}) {
  if (isNotConnected(failure)) {
    return (
      <div className="rounded-lg border border-border/60 bg-muted/30 p-5">
        <div className="flex items-start gap-3">
          <Unplug className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1 space-y-2">
            <p className="text-sm font-medium">Not connected</p>

            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" onClick={onRetry}>
                <RefreshCw className="mr-2 h-3.5 w-3.5" />
                Retry
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-5">
      <div className="flex items-start gap-3">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
        <div className="min-w-0 flex-1 space-y-2">
          <p className="text-sm font-medium text-destructive">{failureHeadline(failure)}</p>

          <p className="text-sm text-muted-foreground">{failure.message}</p>

          {sourceId !== null && (
            <p className="text-xs text-muted-foreground">
              Source <code className="font-mono text-foreground">{sourceId}</code> was not silently
              substituted; what is shown always comes from the source named above.
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={onRetry}>
              <RefreshCw className="mr-2 h-3.5 w-3.5" />
              Retry
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
